import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { AuthSplit, FormField, FormNote, formLinkClass } from '@/components/landing/EditorialForm'
import { cn } from '@/lib/utils'

const steps = ['Enter the email you sign in with', 'Follow the instructions we email you', 'Choose a new password and sign in']

export default function ForgotPasswordPage() {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      setLoading(true)
      // API call to send reset email would go here
      await new Promise(resolve => setTimeout(resolve, 1500))
      setSubmitted(true)
      toast({
        title: 'Success',
        description: 'Password reset instructions have been sent to your email.',
      })
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to send reset instructions. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const aside = (
    <>
      <p className="landing-text-display-xl">
        Locked out?
        <br />
        Three steps
        <br />
        back in.
      </p>
      <ol className="max-w-md">
        {steps.map((step, i) => (
          <li
            key={step}
            className={cn(
              'flex items-baseline gap-5 border-b border-white/20 py-4 text-[17px] first:border-t',
              submitted && i === 0 && 'text-white/60'
            )}
          >
            <span className="w-6 shrink-0 text-sm font-semibold tabular-nums text-white/70">{String(i + 1).padStart(2, '0')}</span>
            {step}
          </li>
        ))}
      </ol>
    </>
  )

  return (
    <LandingLayout>
      {!submitted ? (
        <AuthSplit
          title="Reset password."
          lede={<p>No worries, we'll send you reset instructions.</p>}
          aside={aside}
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <FormField id="email" label="Email address">
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Work email"
                className="landing-field"
              />
            </FormField>

            <button
              type="submit"
              disabled={loading}
              className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep mt-6 w-full disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Sending
                </>
              ) : (
                <>
                  Send reset link
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>

            <FormNote className="mt-6">
              <Link to="/login" className={cn(formLinkClass, 'inline-flex items-center gap-2')}>
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </FormNote>
          </form>
        </AuthSplit>
      ) : (
        <AuthSplit
          title="Check your email."
          lede={<p>We've sent password reset instructions to:</p>}
          aside={aside}
        >
          <div className="flex flex-col gap-6">
            <p className="break-all bg-[#f0f0f0] px-4 py-3.5 font-[family-name:var(--font-landing-subdisplay)] text-lg font-semibold text-[var(--color-landing-gray-900)] dark:bg-[#1d1d26] dark:text-white">
              {email}
            </p>
            <FormNote>
              Didn't receive the email? Check your spam folder or{' '}
              <button type="button" onClick={() => setSubmitted(false)} className={formLinkClass}>
                try another email address
              </button>
            </FormNote>
            <Link to="/login" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep mt-2 w-full">
              <ArrowLeft className="h-5 w-5" />
              Back to sign in
            </Link>
          </div>
        </AuthSplit>
      )}
    </LandingLayout>
  )
}
